import { Body, Button, Form, Spacer, validators } from '@spritz-finance/ui'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAuthContext, useLogin } from '../../app/auth'
import {
    AuthenticationBottomLink,
    AuthenticationBottomText,
    AuthenticationContainer,
    AuthenticationForm,
    AuthenticationTerms,
    FormContent,
    Link,
    LinkingMessage,
} from '../AuthenticationStyled'
import Divider from '../Divider'
import GoogleButton from '../GoogleButton/GoogleButton'
import { PublicLayoutHeader } from '../PublicLayout/Header'
import ConfirmEmail from '../SignUp/ConfirmEmail'

type SignInFormData = {
    email: string
    password: string

    endUser?: string
}

const SignInPage = () => {
    const navigate = useNavigate()
    const [formData, setFormData] = useState<SignInFormData>()
    const { login, error, loading } = useLogin()
    const { linkingCompleted } = useAuthContext()
    const [notConfirmed, setNotConfirmed] = useState<boolean>(false)
    const subTitle = notConfirmed ? 'Please verify your email' : 'Log in to your Spritz account'

    const methods = useForm<SignInFormData>({
        reValidateMode: 'onBlur',
    })

    const canImpersonate = window.location.search.match(/impersonate/)

    useEffect(() => {
        if (!error) return
        if (error.type === 'UserNotConfirmedException') {
            setNotConfirmed(true)
            return
        }

        methods.setError('email', error)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const onSubmit: SubmitHandler<SignInFormData> = async (data) => {
        setFormData(data)
        const args: [string, string, any?] = [data.email, data.password]
        if (canImpersonate) {
            args.push({
                endUser: data?.endUser,
            })
        }
        await login(...args)
    }

    if (notConfirmed) {
        return (
            <>
                <PublicLayoutHeader title="Welcome back!" subtitle={subTitle} />
                <AuthenticationContainer>
                    <ConfirmEmail {...formData!} notConfirmed />
                </AuthenticationContainer>
            </>
        )
    }

    return (
        <>
            <PublicLayoutHeader title="Welcome back!" subtitle={subTitle} />
            <AuthenticationContainer>
                <AuthenticationForm>
                    {linkingCompleted && (
                        <>
                            <LinkingMessage>
                                <Body.Med size={14}>
                                    Account linking completed, you may now sign in with either
                                    Google or your username and password
                                </Body.Med>
                            </LinkingMessage>
                            <Spacer height={32} />
                        </>
                    )}
                    <GoogleButton label="Sign In With Google" />

                    <Divider />

                    <Form<SignInFormData> methods={methods} onSubmit={onSubmit} width="auto">
                        <FormContent>
                            <Form.Input
                                autoComplete="off"
                                name="email"
                                placeholder="Email address"
                                validation={{
                                    required: true,
                                    validate: {
                                        ...validators.email,
                                    },
                                }}
                            />
                            <Form.Input
                                autoComplete="off"
                                name="password"
                                placeholder="Password"
                                type="password"
                                validation={{ required: true }}
                            />

                            {canImpersonate && (
                                <>
                                    <Form.Input
                                        autoComplete="off"
                                        name="endUser"
                                        placeholder="End User"
                                    />
                                </>
                            )}

                            <AuthenticationTerms>
                                <Body.Reg size={12}>
                                    Forgot your Password?
                                    <Link onClick={() => navigate('/forgot-password')}>
                                        Reset password
                                    </Link>
                                </Body.Reg>
                            </AuthenticationTerms>

                            <Spacer height={16} />

                            <Button.CTA loading={loading} type="submit">
                                Sign In
                            </Button.CTA>
                        </FormContent>
                    </Form>
                </AuthenticationForm>
            </AuthenticationContainer>
            <Spacer height={48} />
            <AuthenticationBottomText>
                Don't have an account?
                <AuthenticationBottomLink onClick={() => navigate('/signup')}>
                    Sign up
                </AuthenticationBottomLink>
            </AuthenticationBottomText>
        </>
    )
}

export default SignInPage
